import { useMemo, useCallback, useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { Clean } from '@carbon/react/icons';
import { CollapsibleFilterPanel } from 'components/collapsible-filter-panel';
import ReconcileTearsheet from 'pages/entities/show/reconcile/entity-consolidation-tearsheet';
import { useEntityListReconcile } from './use-entity-list-reconcile';
import { createEntitySearchAtom, entitySummaryAtom } from './atoms';
import { countAppliedFilters } from './utils';
import { useEntityListSearchHandlers } from './hooks';
import {
  EntitySummaryCards,
  type EntitySummaryCardType,
} from './summary-cards';
import { EntityFilters } from './entity-filters';
import { EntityTable } from './entity-table';
import type { EntitySearchResult } from './types';
import { NoReconcileCandidatesModal } from 'pages/entities/show/reconcile/no-reconcile-candidates-modal';
import { UndoReconcileModal } from 'pages/entities/show/reconcile/undo-reconcile-modal';
import styles from './entity-dashboard.module.scss';

/**
 * The Entity Library list: summary cards, filters, the table and its inline
 * Reconcile.
 * @return {JSX.Element} The page.
 */
export function EntityDashboardContent() {
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const { searchAtom } = useMemo(() => createEntitySearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  // Re-run the current search (post-reconcile/undo) so retired entities drop
  // out or show their Deduplicated badge.
  const refreshList = useCallback(() => {
    setSearchPayload((current) => ({ ...current }));
  }, [setSearchPayload]);

  // Inline row-action Reconcile (mock: entity-list-page-reconciliation) — a
  // shortcut into the same tearsheet the Entity Profile page opens.
  const reconcile = useEntityListReconcile(refreshList);

  // Depends on the stable launchFor callback, NOT the reconcile object (a fresh
  // literal every render) — otherwise this invalidates the table's memoized
  // columns on every dashboard render.
  const { launchFor } = reconcile;
  const renderRowAction = useCallback(
    (entity: EntitySearchResult) => (
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={Clean}
        iconDescription="Reconcile"
        // The column sits at the table's right edge — a right-facing tooltip
        // gets clipped by the scroll container.
        tooltipPosition="left"
        disabled={!!entity.deduplicatedAt}
        onClick={() => launchFor(entity.id)}
      />
    ),
    [launchFor]
  );

  const [summaryState, fetchSummary] = useAtom(entitySummaryAtom);

  useEffect(() => {
    fetchSummary({
      url: '/queries/entities/summary',
      type: 'POST',
      data: {},
    });
  }, [fetchSummary]);

  const summaryData =
    summaryState.result?.type === 'success'
      ? summaryState.result.data
      : undefined;

  const filterCount = useMemo(
    () => countAppliedFilters(searchResult.currentPayload),
    [searchResult.currentPayload]
  );

  const {
    handleCreatedAtChange,
    handleUpdatedAtChange,
    handleEntityTypesChange,
    handleOperatorOnlyChange,
    handleResetFilters,
    handleSearchChange,
    handleSortChange,
    handlePaginationChange,
    handleEntityTypeFilterChange,
    handleDeduplicatedChange,
  } = useEntityListSearchHandlers(setSearchPayload);

  const payload = searchResult.currentPayload;

  const handleSummaryCardClick = useCallback(
    (card: EntitySummaryCardType) => {
      const now = moment();
      const daysBack = { last24h: 1, last7d: 7, lastMonth: 30 };
      const start = now.clone().subtract(daysBack[card], 'days');
      handleCreatedAtChange({
        start: start.format('YYYY-MM-DD'),
        end: now.format('YYYY-MM-DD'),
      });
    },
    [handleCreatedAtChange]
  );

  return (
    <div className={styles.pageWrapper}>
      {reconcile.assembling && (
        <Loading
          description="Assembling reconciliation candidates…"
          withOverlay
        />
      )}

      {reconcile.launchError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Reconcile unavailable —"
          subtitle={reconcile.launchError}
          onCloseButtonClick={reconcile.dismissLaunchError}
        />
      )}

      {/* Post-reconcile summary banner (mock: list-page reconcile) — bullets of
          what was performed. Undo Reconcile lives in the page header. */}
      {reconcile.summary && (
        <InlineNotification
          kind="success"
          lowContrast
          title="Reconcile complete —"
          subtitle={reconcile.summary.bullets.join(' ')}
          onCloseButtonClick={reconcile.dismissSummary}
        />
      )}

      <div className={styles.summaryCardsWrapper}>
        <EntitySummaryCards
          stats={summaryData}
          onCardClick={handleSummaryCardClick}
        />
      </div>
      <div className={styles.pageLayout}>
        <CollapsibleFilterPanel
          filterCount={filterCount}
          onReset={handleResetFilters}
          isOpen={isFilterOpen}
          // The date filters' calendars render in the body, so the rail can scroll
          // its own overflow instead of growing the page.
          scrollable
        >
          <EntityFilters
            payload={payload}
            onCreatedAtChange={handleCreatedAtChange}
            onUpdatedAtChange={handleUpdatedAtChange}
            onEntityTypesChange={handleEntityTypesChange}
            onOperatorOnlyChange={handleOperatorOnlyChange}
            onEntityTypeFilterChange={handleEntityTypeFilterChange}
            onDeduplicatedChange={handleDeduplicatedChange}
          />
        </CollapsibleFilterPanel>
        <div className={styles.mainContent}>
          <EntityTable
            searchResult={searchResult}
            isFilterOpen={isFilterOpen}
            onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
            filterCount={filterCount}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onPaginationChange={handlePaginationChange}
            renderRowAction={renderRowAction}
          />
        </div>
      </div>

      {/* Same reconcile tearsheet the Entity Profile page opens, anchored on the
          row's entity (fetched in full on click). Mounted only while open — a
          closed Carbon Modal still renders its children hidden in the DOM. */}
      {reconcile.viewedEntity && reconcile.tearsheetOpen && (
        <ReconcileTearsheet
          open
          onClose={reconcile.closeTearsheet}
          viewedEntity={reconcile.viewedEntity}
          candidates={reconcile.candidates}
          viewedAliasGroup={reconcile.viewedAliasGroup}
          moreFound={reconcile.moreFound}
          totalBeforeCap={reconcile.totalBeforeCap}
          sessionId={reconcile.sessionId}
          onComplete={reconcile.complete}
          onCanonized={reconcile.notchCanonize}
        />
      )}

      {/* All-candidates-eliminated (narrative Case 4). */}
      <NoReconcileCandidatesModal
        open={reconcile.allEliminatedOpen}
        onClose={reconcile.closeAllEliminated}
      />

      {/* Undo Reconcile confirmation (mock) — bullets out what will reverse. */}
      <UndoReconcileModal
        open={reconcile.undoModalOpen}
        undoing={reconcile.undoing}
        bullets={reconcile.summary?.bullets ?? []}
        addAsNewCount={reconcile.summary?.addAsNewCount ?? 0}
        undoError={reconcile.undoError}
        onCancel={reconcile.cancelUndo}
        onConfirm={reconcile.confirmUndo}
      />
    </div>
  );
}
